import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { AuthContext } from '../../context/Auth/AuthContext'
import toastError from '../../errors/toastError'
import { isSuperOrAdmin } from '../../helpers/isSuperOrAdmin'
import api from '../../services/api'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { InputField } from '../InputField'
import { useStyles } from './style'

const getContactCustomFieldSchema = (canEditName, isEditing) => {
  return Yup.object().shape({
    name:
      canEditName || !isEditing
        ? Yup.string()
            .min(2, 'Muito curto!')
            .max(50, 'Muito longo!')
            .required('Campo obrigatório')
        : Yup.string() // Se não pode editar e está editando, não valida o nome
    // value: Yup.string().max(500, 'Muito longo!').required('Campo obrigatório')
  })
}

const initialState = {
  name: '',
  value: ''
}

const ContactCustomFieldModal = ({
  open,
  onClose,
  contactId,
  customField,
  onSave
}) => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const [loading, setLoading] = useState(false)

  const canEditFieldName = isSuperOrAdmin(user)

  const [fieldData, setFieldData] = useState(initialState)

  useEffect(() => {
    if (customField) {
      setFieldData({
        name: customField.name || '',
        value: customField.value || ''
      })
    } else {
      setFieldData(initialState)
    }
  }, [customField, open])

  const handleClose = () => {
    setFieldData(initialState)
    onClose()
  }

  const handleSaveCustomField = async values => {
    setLoading(true)
    try {
      let savedField
      let dataToSend = { ...values, value: '' }

      if (customField?.id && !canEditFieldName) {
        delete dataToSend.name
      }

      if (customField?.id) {
        const response = await api.put(
          `/contacts/${contactId}/custom-fields/${customField.id}`,
          dataToSend
        )
        savedField = response.data
        toast.success('Campo customizado atualizado com sucesso!')
      } else {
        const response = await api.post(
          `/contacts/${contactId}/custom-fields`,
          dataToSend
        )
        savedField = response.data
        toast.success('Campo customizado criado com sucesso!')
      }

      if (onSave) {
        onSave(savedField, customField?.id ? 'update' : 'create')
      }
      handleClose()
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      scroll="paper" // Garante que o scroll fique no DialogContent
    >
      <Formik
        initialValues={fieldData}
        enableReinitialize={true}
        validationSchema={getContactCustomFieldSchema(
          canEditFieldName,
          !!customField?.id
        )}
        onSubmit={handleSaveCustomField}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflowY: 'hidden'
            }}
          >
            <DialogTitle className={classes.dialogTitle}>
              {customField?.id
                ? 'Editar Campo Customizado'
                : 'Adicionar Campo Customizado'}
            </DialogTitle>

            <DialogContent dividers>
              {/* Alerta de criação movido para dentro do content para scrollar junto com os inputs */}
              {!customField?.id && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.info}
                >
                  <strong>Importante:</strong> Ao criar um novo campo, ele será
                  adicionado a todos os contatos.
                </Typography>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InputField
                    className={classes.input}
                    id="name"
                    name="name"
                    label="Nome do Campo"
                    value={values.name}
                    onChange={e => {
                      // Força o valor para maiúsculo antes de salvar no estado do Formik
                      const { value } = e.target
                      setFieldValue('name', value.toUpperCase())
                    }}
                    error={touched.name && Boolean(errors.name)}
                    helperText={
                      touched.name && errors.name
                        ? errors.name
                        : !canEditFieldName && customField?.id
                          ? 'Apenas administradores podem alterar o nome'
                          : ''
                    }
                    fullWidth
                    autoFocus={canEditFieldName || !customField?.id}
                    disabled={!canEditFieldName && !!customField?.id}
                  />
                </Grid>
                {/* <Grid item xs={12}>
                  <InputField
                    name="value"
                    label="Valor"
                    value={values.value}
                    error={touched.value && Boolean(errors.value)}
                    helperText={touched.value && errors.value}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid> */}
              </Grid>
            </DialogContent>

            <DialogActions>
              <ButtonSecondary
                label="Cancelar"
                onClick={handleClose}
                color="default"
                disabled={loading}
              />

              <ButtonPrimary
                label={customField?.id ? 'Atualizar' : 'Adicionar'}
                type="submit"
                color="primary"
                variant="contained"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default ContactCustomFieldModal
